import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/ui/Logo';
import { Dropdown } from '../components/ui/Dropdown';
import { Button } from '../components/ui/Button';
import { EyeClosed } from '../components/ui/icons/EyeClosed';
import { 
  User, 
  UserPlus,
  UserCheck,
  Globe, 
  Settings, 
  HelpCircle, 
  Link, 
  Home, 
  Menu, 
  Eye,
  CircleDollarSign
} from 'lucide-react';
import { getAccountDropdownItems } from '../utils/getAccountDropdownItems';
import { useLanguage } from '../hooks/useLanguage';

const API_URL = process.env.REACT_APP_API_URL as string;

export const RegisterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage, switchLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<'register' | 'confirm' | 'user'>('register');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>('');

  // Проверка PWA режима
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true;

  // Проверка Safe Area (только для iPhone)
  const hasSafeArea = window.matchMedia('(display-mode: standalone)').matches && 
                      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top') !== '0px';
  const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top');


  const languageItems = [
    {
      id: 'eng',
      label: 'English',
      onClick: () => switchLanguage('ENG')
    },
    {
      id: 'rus',
      label: 'Русский',
      onClick: () => switchLanguage('RUS')
    }
  ];

  const pagesItems = [
    { id: 'home', label: t('home'), icon: Home, onClick: () => navigate('/') },
    { id: 'instructions', label: t('instructions'), icon: Settings, onClick: () => navigate('/instructions') },
    { id: 'faq', label: t('faq'), icon: HelpCircle, onClick: () => navigate('/faq') },
    { id: 'pricing', label: t('pricing'), icon: CircleDollarSign, onClick: () => navigate('/pricing') },
    { id: 'links', label: t('links'), icon: Link, onClick: () => navigate('/links') }
  ];

  const accountItems = getAccountDropdownItems(t, navigate);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail && typeof data.detail === 'object' && data.detail.code) {
          throw new Error(t(data.detail.code));
        } else {
          throw new Error(t('unknown_error'));
        }
      }
      setSuccessMsg(data.message);
      setStep('confirm');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await fetch(`${API_URL}/confirm_email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.detail && typeof data.detail === 'object' && data.detail.code) {
          throw new Error(t(data.detail.code));
        } else {
          throw new Error(t('unknown_error'));
        }
      }
      setSuccessMsg(data.message);
      setUserId(data.user_id || '');
      // Сохраняем пользователя в localStorage
      localStorage.setItem('user', JSON.stringify({ user_id: data.user_id, email, password, twofa_enabled: data.twofa_enabled }));
      
      // Сохраняем текущий язык в БД при регистрации
      try {
        const currentLang = currentLanguage === 'ENG' ? 'en' : 'ru';
        await fetch(`${API_URL}/user/language`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: parseInt(data.user_id),
            language: currentLang
          })
        });
      } catch (langError) {
        console.warn('Не удалось сохранить язык при регистрации:', langError);
      }
      navigate('/user');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-header border-b border-border">
        <div className="flex items-center justify-between w-full h-20 px-4">
          {/* Левая колонка */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-4">
              <Logo color="primary" className="" style={{ color: '#525252', marginTop: '1mm', width: 'clamp(48px, 5.3vw, 64px)', height: 'clamp(48px, 5.3vw, 64px)' }} />
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-3xl font-extra-bold neon-glow whitespace-nowrap" style={{ color: '#525252' }}>
                  ForsBot
                </h1>
                <p
                  className="text-textSubtle truncate"
                  style={{ fontSize: 'clamp(8px, 2vw, 16px)' }}
                >
                  {t('main_slogan')}
                </p>
              </div>
            </div>
          </div>
          {/* Правая колонка */}
          <div className="flex items-center space-x-1 flex-shrink-0">
              <Dropdown
                id="account"
                trigger={<User className="w-5 h-5 text-iconPrimary" />}
                items={accountItems}
                align="right"
              />
              <Dropdown
                id="language"
                trigger={<span className="text-sm font-medium text-iconPrimary">{currentLanguage}</span>}
                items={languageItems}
                align="right"
              />
              <Dropdown
                id="pages"
                trigger={<Menu className="w-5 h-5 text-iconPrimary" />}
                items={pagesItems}
                align="right"
              />
          </div>
        </div>
      </header>

      <main
        className="w-full flex-1 flex flex-col py-2 sm:py-8 px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24"
        style={{ 
          paddingTop: (isPWA && hasSafeArea) ? `calc(6rem + ${safeAreaTop})` : '6rem',
          paddingLeft: '0.5cm', 
          paddingRight: '0.5cm' 
        }}
      >
        {/* Форма регистрации */}
        <div className="w-full max-w-md mx-auto mt-6 sm:mt-8">
          <div className="bg-header border border-border rounded-lg p-4 sm:p-6 md:p-8">
            {step === 'register' && (
              <>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-text mb-4 sm:mb-6 text-center font-sans">
                  {t('registration')}
                </h1>
                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-text mb-1 sm:mb-2 font-sans">
                      {t('email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('enter_email')}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-text placeholder-textSubtle font-sans text-sm sm:text-base input-neon-focus"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-text mb-1 sm:mb-2 font-sans">
                      {t('password')}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('enter_password')}
                        className="w-full px-3 py-2 pr-10 border border-border rounded-md bg-background text-text placeholder-textSubtle font-sans text-sm sm:text-base input-neon-focus"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-textSubtle focus:outline-none active:bg-transparent hover:bg-transparent"
                      >
                        {showPassword ? (
                          <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                        ) : (
                          <EyeClosed className="h-4 w-4 sm:h-5 sm:w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full mt-4 sm:mt-6">
                    {t('create_account_button')}
                  </Button>
                </form>
                <div className="mt-4 sm:mt-6 text-center">
                  <p className="text-textSubtle font-sans text-sm sm:text-base">
                    {t('have_account')}{' '}
                    <button
                      onClick={handleLoginClick}
                      className="text-primary hover:underline font-medium cursor-pointer font-sans focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                      style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                    >
                      {t('login_link')}
                    </button>
                  </p>
                </div>
              </>
            )}
            {step === 'confirm' && (
              <>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-text mb-4 sm:mb-6 text-center font-sans">
                  Подтверждение email
                </h1>
                <form onSubmit={handleConfirm} className="space-y-3 sm:space-y-4">
                  <div>
                    <label htmlFor="code" className="block text-sm font-medium text-text mb-1 sm:mb-2 font-sans">
                      Код подтверждения
                    </label>
                    <input
                      type="text"
                      id="code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Введите код из письма"
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-text placeholder-textSubtle font-sans text-sm sm:text-base input-neon-focus"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full mt-4 sm:mt-6">
                    Подтвердить
                  </Button>
                </form>
              </>
            )}
            {error && <div className="text-red-500 text-center mt-4">{error}</div>}
            {successMsg && <div className="text-green-600 text-center mt-4">{successMsg}</div>}
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="fixed bottom-0 left-0 w-full bg-header border-t border-border py-1 sm:py-8 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-base sm:text-lg text-text mb-1 sm:mb-2 font-sans">{t('footer_welcome')}</p>
          <p className="text-textSubtle font-sans text-sm sm:text-base">{t('footer_slogan')}</p>
        </div>
      </footer>
    </div>
  );
}; 